import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as ColumnConfigActions from '../actions/column-config-actions';
import * as ColumnConfigReducerActions from '../reducers/column-config.reducer';
import * as DashboardActions from '../actions/dashboard-actions';
import { map, switchMap, catchError, mergeMap, filter, withLatestFrom } from 'rxjs/operators';
import { of, forkJoin } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '../reducers';
import { dashboardFeatureSelectors } from '../features/dashboard.feature';
import { ColumnConfig, EntityColumnConfigState } from '../reducers/column-config.reducer';
import { DefaultGridConfigService } from '@app/Services/default-grid-config.service';
import { columnConfigFeature } from '../features/column-config.feature';

@Injectable()
export class ColumnConfigEffects {
  constructor(
    private actions$: Actions,
    private defaultGridConfigService: DefaultGridConfigService,
    private store: Store<AppState>
  ) {}

 private generateDefaultColumnConfig(entityName: string, properties: any[]): ColumnConfig[] {
    return properties.map((prop, index) => ({
      field: prop.name,
      visible: true,
      sortable: true,
      defaultSort: index === 0 ? 'asc' as const : undefined,
      sortIndex: index === 0 ? 0 : undefined,
      width: 150,
      minWidth: 50,
      pinned: index === 0 ? 'left' as const : undefined,
      headerName: prop.name,
      tooltip: prop.name
    }));
  }

  // Load column config when entity is selected (only if not already loaded)
  loadColumnConfigOnEntitySelect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.selectedEntity),
      withLatestFrom(
        this.store.select(dashboardFeatureSelectors.selectEntities),
        this.store.select(columnConfigFeature.selectColumnConfigState)
      ),
      switchMap(([action, entities, columnConfigState]) => {
        const entityName = action.selectedEntity?.entityName;
        if (!entityName) {
          return of({ type: '[ColumnConfig] No Entity Name' });
        }

        // Check if config already exists in store
        if (columnConfigState[entityName]) {
          console.log('Column config already loaded for:', entityName);
          return of({ type: '[ColumnConfig] Already Loaded' });
        }

        // Find entity from entities list to get properties
        const entity = entities.find(e => e.entityName === entityName);
        if (!entity || !entity.properties || entity.properties.length === 0) {
          console.log('No properties found for entity:', entityName);
          return of({ type: '[ColumnConfig] No Properties' });
        }

        console.log('Loading column config for entity:', entityName);
        const defaultConfig = this.generateDefaultColumnConfig(entityName, entity.properties);

        // Try to load saved config from localStorage, fallback to default
        return this.defaultGridConfigService.getDefaultGridConfig(entityName).pipe(
          map(savedConfig => {
            const config = savedConfig || defaultConfig;
            console.log(savedConfig ? 'Loaded saved config for:' : 'Using default config for:', entityName);
            return ColumnConfigReducerActions.setColumnConfig({ entity: entityName, config });
          }),
          catchError(() => {
            console.log('Using default config for:', entityName);
            return of(ColumnConfigReducerActions.setColumnConfig({ entity: entityName, config: defaultConfig }));
          })
        );
      }),
      catchError((error) => {
        console.error('Error loading column config on entity select:', error);
        return of({ type: '[ColumnConfig] Load Error', error });
      })
    )
  );
loadDefaultColumnConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ColumnConfigActions.loadDefaultColumnConfig),
      withLatestFrom(
        this.store.select(dashboardFeatureSelectors.selectEntities),
        this.store.select(columnConfigFeature.selectColumnConfigState)
      ),
      switchMap(([{ gridId }, entities, columnConfigState]) => {
        // Check if config already exists in store
        if (columnConfigState[gridId]) {
          console.log('Column config already loaded for:', gridId);
          return of(ColumnConfigActions.loadDefaultColumnConfigSuccess({ gridId, config: columnConfigState[gridId] }));
        }

        // Find entity to generate default config if needed
        const entity = entities.find(e => e.entityName === gridId);
        const defaultConfig = entity && entity.properties && entity.properties.length > 0
          ? this.generateDefaultColumnConfig(gridId, entity.properties)
          : [];

        return this.defaultGridConfigService.getDefaultGridConfig(gridId).pipe(
          map((config) => {
            const finalConfig = config || defaultConfig;
            return ColumnConfigActions.loadDefaultColumnConfigSuccess({ gridId, config: finalConfig });
          }),
          catchError((error) => {
            console.error('Error loading default column config:', error);
            // Return default config on error
            if (defaultConfig.length > 0) {
              return of(ColumnConfigActions.loadDefaultColumnConfigSuccess({ gridId, config: defaultConfig }));
            }
            return of({ type: '[DefaultColumnConfig] Load Error', error });
          })
        );
      })
    )
  );
saveDefaultColumnConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ColumnConfigActions.saveDefaultColumnConfig),
      switchMap(({ gridId, config }) =>
        this.defaultGridConfigService.saveDefaultGridConfig(gridId, config).pipe(
          map(() => ColumnConfigActions.saveDefaultColumnConfigSuccess({ gridId, config })),
          catchError((error) => {
            console.error('Error saving default column config:', error);
            return of({ type: '[DefaultColumnConfig] Save Error', error });
          })
        )
      )
    )
  );

  loadDefaultColumnConfigSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ColumnConfigActions.loadDefaultColumnConfigSuccess),
      map(({ gridId, config }) => ColumnConfigReducerActions.setColumnConfig({ entity: gridId, config }))
    )
  );
saveDefaultColumnConfigSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ColumnConfigActions.saveDefaultColumnConfigSuccess),
      map(({ gridId, config }) => ColumnConfigReducerActions.setColumnConfig({ entity: gridId, config }))
    )
  );

  deleteDefaultColumnConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ColumnConfigActions.deleteDefaultColumnConfig),
      switchMap(({ gridId }) =>
        this.defaultGridConfigService.deleteDefaultGridConfig(gridId).pipe(
          map(() => ColumnConfigActions.deleteDefaultColumnConfigSuccess({ gridId })),
          catchError((error) => {
            console.error('Error deleting default column config:', error);
            return of({ type: '[DefaultColumnConfig] Delete Error', error });
          })
        )
      )
    )
  );
// Reset column config by regenerating defaults from entities
  resetColumnConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ColumnConfigReducerActions.resetColumnConfig),
      withLatestFrom(this.store.select(dashboardFeatureSelectors.selectEntities)),
      switchMap(([action, entities]) => {
        const entity = entities.find(e => e.entityName === action.entity);
        if (entity && entity.properties && entity.properties.length > 0) {
          const defaultConfig = this.generateDefaultColumnConfig(entity.entityName, entity.properties);
          return of(ColumnConfigReducerActions.setColumnConfig({ entity: action.entity, config: defaultConfig }));
        }
        return of({ type: '[ColumnConfig] Reset Error' });
      })
    )
  );
}
